<template>
  <div class="enrollment-page">
    <!-- Navigation Bar -->
    <nav class="navbar navbar-expand-lg navbar-light bg-white shadow-sm fixed-top">
      <div class="container">
        <a class="navbar-brand d-flex align-items-center" href="#home">
          <img src="https://pona.paysol.africa/logos/pona.png" alt="PONA HEALTH-X Logo" height="50" class="me-3">
        </a>
        <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
          <span class="navbar-toggler-icon"></span>
        </button>
        <div class="collapse navbar-collapse" id="navbarNav">

          <ul class="navbar-nav ms-auto align-items-center">
            <li class="nav-item">
              <a class="nav-link" href="javascript: void(0);" @click="goToPage('/')">Home</a>
            </li>

            <li class="nav-item">
              <a class="nav-link" href="javascript: void(0);" @click="goToPage('about')">About</a>
            </li>
            <li class="nav-item d-none">
              <a class="nav-link" href="#how-it-works">How It Works</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#benefits">Benefits</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#services">Health Services</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#enrollment">Enrollment</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#facilities">Health Facilities</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#faqs">FAQs</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" href="#contact">Contact</a>
            </li>

            <li class="nav-item dropdown ms-3">
              <button class="btn btn-primary dropdown-toggle" type="button"
                      id="loginDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                <i class="bx bx-user me-2"></i>Login
              </button>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="loginDropdown">
                <li>
                  <a class="dropdown-item" @click="goToMemberLogin">
                    <i class="bx bx-user-circle me-2"></i>Member Portal
                  </a>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li>
                  <a class="dropdown-item" @click="goToStaffLogin">
                    <i class="bx bx-briefcase me-2"></i>Staff Login
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- Page Header -->
    <section class="page-header">
      <div class="header-overlay"></div>
      <div class="container">
        <div class="row align-items-center min-vh-50 py-5">
          <div class="col-lg-8 text-white position-relative z-2">
            <nav aria-label="breadcrumb" class="mb-4">
              <ol class="breadcrumb breadcrumb-light">
                <li class="breadcrumb-item"><router-link to="/">Home</router-link></li>
                <li class="breadcrumb-item active" aria-current="page">Enrollment</li>
              </ol>
            </nav>
            <div class="header-badge mb-3">
              <i class="bx bx-map-pin"></i>
              <span>In-Person Registration</span>
            </div>
            <h1 class="display-3 fw-bold mb-4 animate-on-scroll text-white">
              How to Enroll in PONA HEALTH-X
            </h1>
            <p class="lead mb-4 animate-on-scroll">
              Join thousands of families protected by Community-Based Health Insurance.
              Visit your nearest enrollment center to register your household today.
            </p>
            <div class="header-info-cards d-flex flex-wrap gap-3 animate-on-scroll">
              <div class="info-card">
                <i class="bx bx-time-five"></i>
                <div>
                  <span class="info-label">Processing Time</span>
                  <span class="info-value">Same Day</span>
                </div>
              </div>
              <div class="info-card">
                <i class="bx bx-calendar"></i>
                <div>
                  <span class="info-label">Enrollment Period</span>
                  <span class="info-value">Year Round</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Important Notice -->
    <section class="notice-section">
      <div class="container">
        <div class="notice-card animate-on-scroll">
          <div class="notice-icon">
            <i class="bx bx-info-circle"></i>
          </div>
          <div class="notice-content">
            <h5 class="fw-bold mb-2">Important: In-Person Registration Required</h5>
            <p class="mb-0">
              PONA HEALTH-X enrollment is done <strong>in person</strong> at your local community administration office or
              designated health facility. Online registration is not available. Please visit your nearest
              enrollment center with the required documents to complete your registration.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Enrollment Overview -->
    <section class="py-5 bg-white">
      <div class="container py-5">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-5 mb-lg-0 animate-on-scroll">
            <div class="section-label">
              <span class="label-icon"><i class="bx bx-user-plus"></i></span>
              <span class="label-text">Get Started</span>
            </div>
            <h2 class="display-5 fw-bold mb-4">
              Simple
              <span class="text-gradient">Enrollment Process</span>
            </h2>
            <p class="lead text-muted mb-4">
              Enrolling in PONA HEALTH-X is easy and straightforward. Visit your local enrollment center,
              bring the required documents, and you'll be registered the same day. Your coverage
              begins immediately after payment.
            </p>
            <div class="quick-facts">
              <div class="fact-item">
                <div class="fact-icon bg-success">
                  <i class="bx bx-check"></i>
                </div>
                <span>No medical examination required</span>
              </div>
              <div class="fact-item">
                <div class="fact-icon bg-success">
                  <i class="bx bx-check"></i>
                </div>
                <span>Pre-existing conditions covered</span>
              </div>
              <div class="fact-item">
                <div class="fact-icon bg-success">
                  <i class="bx bx-check"></i>
                </div>
                <span>Whole family covered under one membership</span>
              </div>
              <div class="fact-item">
                <div class="fact-icon bg-success">
                  <i class="bx bx-check"></i>
                </div>
                <span>Coverage starts immediately after payment</span>
              </div>
            </div>
          </div>
          <div class="col-lg-6 animate-on-scroll">
            <div class="enrollment-image-container">
              <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600"
                   alt="Enrollment Process"
                   class="img-fluid rounded-4 main-img">
              <div class="image-badge">
                <i class="bx bx-buildings"></i>
                <span>Visit Your Local Center</span>
              </div>
              <div class="floating-card">
                <div class="fc-icon"><i class="bx bx-group"></i></div>
                <div class="fc-content">
                  <span class="fc-number">15M+</span>
                  <span class="fc-label">Members Enrolled</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Enrollment Steps -->
    <section class="py-5 bg-light" id="steps">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-list-ol"></i></span>
            <span class="label-text">Step by Step</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Enrollment Steps</h2>
          <p class="lead text-muted">Follow these simple steps to become a PONA HEALTH-X member</p>
        </div>

        <div class="enrollment-steps">
          <div class="step-card animate-on-scroll" v-for="(step, index) in enrollmentSteps" :key="step.title">
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-icon" :class="'bg-' + step.color">
              <i :class="step.icon"></i>
            </div>
            <div class="step-content">
              <h4 class="fw-bold mb-3">{{ step.title }}</h4>
              <p class="text-muted mb-3">{{ step.description }}</p>
              <ul class="step-details" v-if="step.details">
                <li v-for="detail in step.details" :key="detail">
                  <i class="bx bx-check-circle text-success"></i>
                  <span>{{ detail }}</span>
                </li>
              </ul>
            </div>
            <div class="step-connector" v-if="index < enrollmentSteps.length - 1">
              <i class="bx bx-chevron-down"></i>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Where to Enroll -->
    <section class="py-5">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-map"></i></span>
            <span class="label-text">Locations</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Where to Enroll</h2>
          <p class="lead text-muted">Visit any of these enrollment points in your area</p>
        </div>

        <div class="row g-4">
          <div class="col-lg-4 animate-on-scroll" v-for="location in enrollmentLocations" :key="location.title">
            <div class="location-card h-100">
              <div class="location-icon" :class="'bg-' + location.color">
                <i :class="location.icon"></i>
              </div>
              <h4 class="fw-bold mb-3">{{ location.title }}</h4>
              <p class="text-muted mb-4">{{ location.description }}</p>
              <div class="location-details">
                <div class="detail-item">
                  <i class="bx bx-time"></i>
                  <span>{{ location.hours }}</span>
                </div>
                <div class="detail-item">
                  <i class="bx bx-calendar-check"></i>
                  <span>{{ location.days }}</span>
                </div>
              </div>
              <div class="location-services">
                <span class="service-tag" v-for="service in location.services" :key="service">
                  {{ service }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Find Your Office -->
        <div class="find-office-card animate-on-scroll">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <h4 class="fw-bold mb-2">Need Help Finding Your Enrollment Center?</h4>
              <p class="text-muted mb-lg-0">
                Contact the PONA HEALTH-X hotline or visit your nearest health center for directions
                to the enrollment office in your district.
              </p>
            </div>
            <div class="col-lg-4 text-lg-end">
              <a href="tel:+254700000000" class="btn btn-primary btn-lg">
                <i class="bx bx-phone me-2"></i>Call +254 700 000 000
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Required Documents -->
    <section class="py-5 bg-gradient-primary text-white">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label-light justify-content-center">
            <span class="label-icon"><i class="bx bx-file"></i></span>
            <span class="label-text">Documentation</span>
          </div>
          <h2 class="display-5 fw-bold mb-3 text-white">Required Documents</h2>
          <p class="lead opacity-75">Prepare these documents before visiting the enrollment center</p>
        </div>

        <div class="row g-4">
          <div class="col-lg-6 animate-on-scroll">
            <div class="documents-card">
              <div class="doc-header">
                <div class="doc-icon bg-success">
                  <i class="bx bx-check-shield"></i>
                </div>
                <h5 class="fw-bold mb-0 text-white">Required Documents</h5>
              </div>
              <ul class="doc-list">
                <li v-for="doc in requiredDocuments" :key="doc.name">
                  <div class="doc-item">
                    <i class="bx bx-file text-success"></i>
                    <div class="doc-info">
                      <span class="doc-name">{{ doc.name }}</span>
                      <span class="doc-note" v-if="doc.note">{{ doc.note }}</span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-6 animate-on-scroll">
            <div class="documents-card">
              <div class="doc-header">
                <div class="doc-icon bg-info">
                  <i class="bx bx-plus-circle"></i>
                </div>
                <h5 class="fw-bold mb-0 text-white">Additional Documents (If Applicable)</h5>
              </div>
              <ul class="doc-list">
                <li v-for="doc in additionalDocuments" :key="doc.name">
                  <div class="doc-item">
                    <i class="bx bx-file text-info"></i>
                    <div class="doc-info">
                      <span class="doc-name">{{ doc.name }}</span>
                      <span class="doc-note" v-if="doc.note">{{ doc.note }}</span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="doc-tip animate-on-scroll">
          <i class="bx bx-bulb"></i>
          <p class="mb-0">
            <strong>Tip:</strong> Make photocopies of all documents before your visit.
            Original documents will be verified but copies will be kept for your file.
          </p>
        </div>
      </div>
    </section>

    <!-- Premium Plans -->
    <section class="py-5">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-money"></i></span>
            <span class="label-text">Premium Plans</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Affordable Premium Options</h2>
          <p class="lead text-muted">Choose the plan that fits your household's situation</p>
        </div>

        <div class="row g-4 justify-content-center">
          <div class="col-lg-4 col-md-6 animate-on-scroll" v-for="plan in premiumPlans" :key="plan.name">
            <div class="premium-card h-100" :class="{ 'featured': plan.featured }">
              <div class="premium-badge" v-if="plan.badge">{{ plan.badge }}</div>
              <div class="premium-header">
                <div class="premium-icon" :class="plan.colorClass">
                  <i :class="plan.icon"></i>
                </div>
                <h4 class="fw-bold mb-1">{{ plan.name }}</h4>
                <p class="text-muted mb-0">{{ plan.subtitle }}</p>
              </div>
              <div class="premium-price">
                <span class="currency">KES</span>
                <span class="amount">{{ plan.price }}</span>
                <span class="period">/year</span>
              </div>
              <div class="premium-description">
                <p>{{ plan.description }}</p>
              </div>
              <ul class="premium-features">
                <li v-for="feature in plan.features" :key="feature">
                  <i class="bx bx-check-circle text-success"></i>
                  <span>{{ feature }}</span>
                </li>
              </ul>
              <div class="premium-eligibility">
                <span class="eligibility-label">Eligibility:</span>
                <span class="eligibility-text">{{ plan.eligibility }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Payment Info -->
        <div class="payment-info-card animate-on-scroll">
          <div class="row">
            <div class="col-lg-6 mb-4 mb-lg-0">
              <h5 class="fw-bold mb-3"><i class="bx bx-credit-card me-2 text-primary"></i>Payment Methods</h5>
              <div class="payment-methods">
                <div class="payment-method" v-for="method in paymentMethods" :key="method.name">
                  <i :class="method.icon"></i>
                  <span>{{ method.name }}</span>
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <h5 class="fw-bold mb-3"><i class="bx bx-calendar me-2 text-primary"></i>Payment Schedule</h5>
              <p class="text-muted mb-2">Premiums are paid annually at the time of enrollment or renewal.</p>
              <ul class="payment-schedule">
                <li><i class="bx bx-check text-success"></i>Pay full amount at enrollment</li>
                <li><i class="bx bx-check text-success"></i>Receive payment receipt</li>
                <li><i class="bx bx-check text-success"></i>Coverage valid for 12 months</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Who Can Enroll -->
    <section class="py-5 bg-light">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-user-check"></i></span>
            <span class="label-text">Eligibility</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Who Can Enroll?</h2>
          <p class="lead text-muted">PONA HEALTH-X is designed for informal sector workers and rural communities</p>
        </div>

        <div class="row g-4">
          <div class="col-lg-6 animate-on-scroll">
            <div class="eligibility-card eligible">
              <div class="elig-header">
                <i class="bx bx-check-circle"></i>
                <h5 class="fw-bold mb-0">Eligible to Enroll</h5>
              </div>
              <ul class="elig-list">
                <li v-for="item in eligibleGroups" :key="item">
                  <i class="bx bx-check"></i>
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-lg-6 animate-on-scroll">
            <div class="eligibility-card not-eligible">
              <div class="elig-header">
                <i class="bx bx-info-circle"></i>
                <h5 class="fw-bold mb-0">Should Enroll in NHIF Instead</h5>
              </div>
              <ul class="elig-list">
                <li v-for="item in nhifGroups" :key="item">
                  <i class="bx bx-right-arrow-alt"></i>
                  <span>{{ item }}</span>
                </li>
              </ul>
              <div class="elig-note">
                <p class="mb-0">
                  <i class="bx bx-info-circle me-2"></i>
                  Formal sector employees are covered under NHIF through their employers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Family Coverage -->
    <section class="py-5">
      <div class="container py-5">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-5 mb-lg-0 animate-on-scroll">
            <div class="section-label">
              <span class="label-icon"><i class="bx bx-group"></i></span>
              <span class="label-text">Family</span>
            </div>
            <h2 class="display-5 fw-bold mb-4">
              Household
              <span class="text-gradient">Coverage</span>
            </h2>
            <p class="lead text-muted mb-4">
              One PONA HEALTH-X membership covers your entire household. All family members living under
              the same roof are included in your coverage.
            </p>

            <div class="family-coverage-list">
              <div class="coverage-item" v-for="member in familyMembers" :key="member.title">
                <div class="coverage-icon" :class="'bg-' + member.color">
                  <i :class="member.icon"></i>
                </div>
                <div class="coverage-content">
                  <h6 class="fw-bold mb-1">{{ member.title }}</h6>
                  <p class="text-muted small mb-0">{{ member.description }}</p>
                </div>
                <div class="coverage-status">
                  <span class="status-badge" :class="member.status">{{ member.statusText }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-6 animate-on-scroll">
            <div class="family-illustration">
              <div class="family-center">
                <div class="family-icon">
                  <i class="bx bx-home-heart"></i>
                </div>
                <span>Your Household</span>
              </div>
              <div class="family-member" v-for="(member, index) in familyIllustration" :key="member.label"
                   :style="{ transform: `rotate(${index * 60}deg) translateY(-120px) rotate(-${index * 60}deg)` }">
                <div class="member-icon">
                  <i :class="member.icon"></i>
                </div>
                <span>{{ member.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Renewal Section -->
    <section class="py-5 bg-light">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-refresh"></i></span>
            <span class="label-text">Renewal</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Membership Renewal</h2>
          <p class="lead text-muted">Keep your coverage active by renewing annually</p>
        </div>

        <div class="row g-4">
          <div class="col-lg-4 animate-on-scroll" v-for="renewal in renewalInfo" :key="renewal.title">
            <div class="renewal-card h-100">
              <div class="renewal-icon" :class="'bg-' + renewal.color">
                <i :class="renewal.icon"></i>
              </div>
              <h5 class="fw-bold mb-3">{{ renewal.title }}</h5>
              <p class="text-muted">{{ renewal.description }}</p>
            </div>
          </div>
        </div>

        <div class="renewal-reminder animate-on-scroll">
          <div class="reminder-icon">
            <i class="bx bx-bell"></i>
          </div>
          <div class="reminder-content">
            <h5 class="fw-bold mb-2">Don't Let Your Coverage Lapse!</h5>
            <p class="mb-0">
              Renew your membership before it expires to maintain continuous coverage.
              Visit your local enrollment center 30 days before your expiry date.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-5">
      <div class="container py-5">
        <div class="text-center mb-5 animate-on-scroll">
          <div class="section-label justify-content-center">
            <span class="label-icon"><i class="bx bx-help-circle"></i></span>
            <span class="label-text">FAQs</span>
          </div>
          <h2 class="display-5 fw-bold mb-3">Enrollment Questions</h2>
          <p class="lead text-muted">Common questions about the enrollment process</p>
        </div>

        <div class="row">
          <div class="col-lg-10 mx-auto">
            <div class="accordion" id="enrollmentFAQ">
              <div class="accordion-item animate-on-scroll" v-for="(faq, index) in faqs" :key="index">
                <h2 class="accordion-header">
                  <button class="accordion-button" :class="{ collapsed: index !== 0 }"
                          type="button" data-bs-toggle="collapse"
                          :data-bs-target="'#faq' + index">
                    <i class="bx bx-help-circle me-3 text-primary"></i>
                    {{ faq.question }}
                  </button>
                </h2>
                <div :id="'faq' + index"
                     class="accordion-collapse collapse"
                     :class="{ show: index === 0 }"
                     data-bs-parent="#enrollmentFAQ">
                  <div class="accordion-body">{{ faq.answer }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-5 cta-section">
      <div class="container py-5">
        <div class="cta-card animate-on-scroll">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <h2 class="display-6 fw-bold text-white mb-3">Ready to Protect Your Family?</h2>
              <p class="lead text-white opacity-75 mb-4 mb-lg-0">
                Visit your nearest enrollment center today with the required documents.
                Our staff are ready to help you join the PONA HEALTH-X family.
              </p>
            </div>
            <div class="col-lg-4 text-lg-end">
              <a href="tel:+254700000000" class="btn btn-light btn-lg mb-3 me-2">
                <i class="bx bx-phone me-2"></i>Call Us
              </a>
              <router-link to="/contact" class="btn btn-outline-light btn-lg mb-3">
                <i class="bx bx-map me-2"></i>Find Center
              </router-link>
            </div>
          </div>
          <div class="cta-features">
            <div class="cta-feature">
              <i class="bx bx-check-circle"></i>
              <span>Same Day Registration</span>
            </div>
            <div class="cta-feature">
              <i class="bx bx-check-circle"></i>
              <span>Immediate Coverage</span>
            </div>
            <div class="cta-feature">
              <i class="bx bx-check-circle"></i>
              <span>Family Included</span>
            </div>
            <div class="cta-feature">
              <i class="bx bx-check-circle"></i>
              <span>No Medical Exam</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-dark text-white py-5">
      <div class="container">
        <div class="row g-4">
          <div class="col-lg-4">
            <div class="d-flex align-items-center mb-4">
              <img src="https://pona.paysol.africa/logos/pona.png" alt="PONA HEALTH-X Logo" height="40" class="me-3" style="background: white; padding: 5px; border-radius: 8px;">
              <div>
                <div class="fw-bold">PONA HEALTH-X</div>
                <div class="small text-white-50">Community-Based Health Insurance</div>
              </div>
            </div>
            <p class="text-white-50 mb-4">
              Working towards universal health coverage through community-based health insurance.
            </p>
            <div class="social-links">
              <a href="#" class="social-link"><i class="bx bxl-facebook-circle"></i></a>
              <a href="#" class="social-link"><i class="bx bxl-twitter"></i></a>
              <a href="#" class="social-link"><i class="bx bxl-linkedin-square"></i></a>
              <a href="#" class="social-link"><i class="bx bxl-youtube"></i></a>
            </div>
          </div>
          <div class="col-lg-2 col-md-6">
            <h6 class="fw-bold mb-3 text-white">Quick Links</h6>
            <ul class="footer-links">
              <li><router-link to="/">Home</router-link></li>
              <li><router-link to="/about">About Us</router-link></li>
              <li><router-link to="/benefits">Benefits</router-link></li>
              <li><router-link to="/services">Services</router-link></li>
            </ul>
          </div>
          <div class="col-lg-2 col-md-6">
            <h6 class="fw-bold mb-3 text-white">Resources</h6>
            <ul class="footer-links">
              <li><a href="#">FAQs</a></li>
              <li><a href="#">Downloads</a></li>
              <li><router-link to="/enrollment">Enrollment</router-link></li>
              <li><router-link to="/contact">Contact</router-link></li>
            </ul>
          </div>
          <div class="col-lg-4">
            <h6 class="fw-bold mb-3 text-white">Contact Us</h6>
            <ul class="footer-contact">
              <li><i class="bx bx-map me-2"></i>Headquarters</li>
              <li><i class="bx bx-phone me-2"></i>Call: +254 700 000 000</li>
              <li><i class="bx bx-envelope me-2"></i>info@ponahealth.com</li>
            </ul>
          </div>
        </div>
        <hr class="my-4 opacity-25">
        <div class="row align-items-center">
          <div class="col-md-6 text-center text-md-start">
            <p class="mb-0 text-white-50 small">
              © 2025 PONA HEALTH-X. All rights reserved.
            </p>
          </div>
          <div class="col-md-6 text-center text-md-end">
            <a href="#" class="text-white-50 small text-decoration-none me-3">Privacy Policy</a>
            <a href="#" class="text-white-50 small text-decoration-none">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- Scroll to Top -->
    <button class="scroll-top-btn" @click="scrollToTop" v-show="showScrollTop">
      <i class="bx bx-chevron-up"></i>
    </button>
  </div>
</template>

<script>
export default {
  name: 'PonaHealthEnrollmentPage',

  data() {
    return {
      showScrollTop: false,

      enrollmentSteps: [
        {
          icon: 'bx bx-map-pin',
          title: 'Visit Your Local Enrollment Center',
          description: 'Go to your community administration office or the nearest health facility that handles PONA HEALTH-X registration.',
          color: 'primary',
          details: [
            'Community administration office',
            'District health office',
            'Designated health centers'
          ]
        },
        {
          icon: 'bx bx-file',
          title: 'Bring Required Documents',
          description: 'Prepare and bring all necessary documents for yourself and your household members.',
          color: 'info',
          details: [
            'National ID (original and copy)',
            'Passport photos for all members',
            'Household members list'
          ]
        },
        {
          icon: 'bx bx-edit',
          title: 'Complete Registration Form',
          description: 'Fill out the PONA HEALTH-X membership registration form with assistance from enrollment staff.',
          color: 'warning',
          details: [
            'Personal information',
            'Household details',
            'Beneficiary information'
          ]
        },
        {
          icon: 'bx bx-wallet',
          title: 'Pay Annual Premium',
          description: 'Pay the annual premium based on your household\'s economic classification.',
          color: 'success',
          details: [
            'Cash or M-Pesa accepted',
            'Receive official receipt',
            'Premium based on income level'
          ]
        },
        {
          icon: 'bx bx-id-card',
          title: 'Receive Membership Card',
          description: 'Get your PONA HEALTH-X membership card which you\'ll use to access healthcare services.',
          color: 'purple',
          details: [
            'Card issued same day',
            'Lists all covered members',
            'Valid for 12 months'
          ]
        }
      ],

      enrollmentLocations: [
        {
          icon: 'bx bx-buildings',
          title: 'Community Administration',
          description: 'Your local community office is the primary enrollment point for PONA HEALTH-X registration.',
          color: 'primary',
          hours: '8:30 AM - 5:30 PM',
          days: 'Monday - Friday',
          services: ['New Registration', 'Renewal', 'Updates']
        },
        {
          icon: 'bx bx-plus-medical',
          title: 'District Health Office',
          description: 'The district health office provides enrollment services and handles special cases.',
          color: 'success',
          hours: '8:30 AM - 5:30 PM',
          days: 'Monday - Friday',
          services: ['Registration', 'Inquiries', 'Complaints']
        },
        {
          icon: 'bx bx-clinic',
          title: 'Health Centers',
          description: 'Some health centers have PONA HEALTH-X enrollment desks for your convenience.',
          color: 'info',
          hours: '8:00 AM - 4:00 PM',
          days: 'Monday - Saturday',
          services: ['Registration', 'Card Replacement']
        }
      ],

      requiredDocuments: [
        { name: 'National Identification Card', note: 'Original and photocopy' },
        { name: 'Passport-size Photographs', note: '2 photos for each household member' },
        { name: 'List of Household Members', note: 'Names, ages, and relationships' },
        { name: 'Proof of Residence', note: 'If different from ID address' }
      ],

      additionalDocuments: [
        { name: 'Birth Certificate', note: 'For children under 18' },
        { name: 'Marriage Certificate', note: 'If adding spouse' },
        { name: 'Indigency Certificate', note: 'For fee waiver eligibility' },
        { name: 'Previous PONA HEALTH-X Card', note: 'If renewing membership' }
      ],

      premiumPlans: [
        {
          name: 'Indigent Plan',
          subtitle: 'Fully Subsidized',
          icon: 'bx bx-heart',
          price: '0',
          colorClass: 'bg-success',
          badge: 'Free',
          description: 'For households identified as indigent by the community. Full premium covered by government.',
          features: [
            'Full premium waiver',
            'Complete healthcare coverage',
            'Family members included',
            'Same benefits as paying members'
          ],
          eligibility: 'Households with indigency certificate'
        },
        {
          name: 'Standard Plan',
          subtitle: 'Most Common',
          icon: 'bx bx-shield-check',
          price: '15,000',
          colorClass: 'bg-primary',
          featured: true,
          badge: 'Popular',
          description: 'Standard premium for most informal sector households with partial government subsidy.',
          features: [
            'Comprehensive coverage',
            'All family members included',
            'Access to all services',
            'Government subsidy included'
          ],
          eligibility: 'Informal sector workers & rural households'
        },
        {
          name: 'Higher Plan',
          subtitle: 'Upper Income',
          icon: 'bx bx-shield-x',
          price: '19,500',
          colorClass: 'bg-warning',
          badge: null,
          description: 'For households in upper wealth quintiles with ability to pay higher premiums.',
          features: [
            'Full comprehensive coverage',
            'All family members included',
            'Priority services where available',
            'Same excellent coverage'
          ],
          eligibility: 'Higher income informal households'
        }
      ],

      paymentMethods: [
        { name: 'M-Pesa', icon: 'bx bx-mobile' },
        { name: 'Cash', icon: 'bx bx-money' },
        { name: 'Bank Transfer', icon: 'bx bx-bank' },
        { name: 'Card Payment', icon: 'bx bx-credit-card' }
      ],

      eligibleGroups: [
        'Farmers and agricultural workers',
        'Self-employed individuals',
        'Small business owners',
        'Daily laborers',
        'Pastoralists and livestock keepers',
        'Artisans and craftspeople',
        'Market vendors and traders',
        'Informal sector workers',
        'Rural community members',
        'Unemployed individuals'
      ],

      nhifGroups: [
        'Government employees',
        'Private sector employees with formal contracts',
        'NGO employees',
        'Employees of international organizations',
        'Public enterprise workers',
        'Pensioners (may be eligible for PONA HEALTH-X)'
      ],

      familyMembers: [
        {
          icon: 'bx bx-user',
          title: 'Head of Household',
          description: 'The primary member who registers the household',
          color: 'primary',
          status: 'included',
          statusText: 'Covered'
        },
        {
          icon: 'bx bx-female',
          title: 'Spouse',
          description: 'Legally married husband or wife',
          color: 'pink',
          status: 'included',
          statusText: 'Covered'
        },
        {
          icon: 'bx bx-child',
          title: 'Children Under 18',
          description: 'All biological and legally adopted children',
          color: 'info',
          status: 'included',
          statusText: 'Covered'
        },
        {
          icon: 'bx bx-male',
          title: 'Dependents',
          description: 'Other dependents living in the household',
          color: 'warning',
          status: 'eligible',
          statusText: 'Eligible'
        }
      ],

      familyIllustration: [
        { icon: 'bx bx-user', label: 'Father' },
        { icon: 'bx bx-female', label: 'Mother' },
        { icon: 'bx bx-child', label: 'Son' },
        { icon: 'bx bx-child', label: 'Daughter' },
        { icon: 'bx bx-user', label: 'Elder' },
        { icon: 'bx bx-child', label: 'Infant' }
      ],

      renewalInfo: [
        {
          icon: 'bx bx-calendar-event',
          title: 'When to Renew',
          description: 'Renew your membership 30 days before your card expiry date to avoid gaps in coverage.',
          color: 'primary'
        },
        {
          icon: 'bx bx-map-pin',
          title: 'Where to Renew',
          description: 'Visit the same enrollment center where you first registered or your current community office.',
          color: 'success'
        },
        {
          icon: 'bx bx-file',
          title: 'What to Bring',
          description: 'Bring your current PONA HEALTH-X card, National ID, updated photos, and the renewal premium payment.',
          color: 'warning'
        }
      ],

      faqs: [
        {
          question: 'Can I enroll online?',
          answer: 'No, PONA HEALTH-X enrollment must be done in person at your local community administration office or designated health facility. This ensures proper verification of documents and accurate registration of household members.'
        },
        {
          question: 'How long does the enrollment process take?',
          answer: 'The enrollment process typically takes 30-60 minutes. If you have all required documents ready, you can complete registration and receive your membership card on the same day.'
        },
        {
          question: 'Can I enroll my family members who live elsewhere?',
          answer: 'PONA HEALTH-X membership covers household members living under the same roof. Family members living in different locations should enroll separately in their own community.'
        },
        {
          question: 'What if I can\'t afford the premium?',
          answer: 'If you are identified as indigent by your community, you may be eligible for a full premium waiver. Contact your community administration to apply for an indigency certificate.'
        },
        {
          question: 'When does my coverage start?',
          answer: 'Your coverage starts immediately after you complete registration and pay your premium. You can begin accessing healthcare services right away with your membership card.'
        },
        {
          question: 'Can I change my enrollment center later?',
          answer: 'If you move to a different district, you can transfer your membership to your new location. Visit your new community office with your current PONA HEALTH-X card and updated residence documents.'
        },
        {
          question: 'What happens if I don\'t renew on time?',
          answer: 'If your membership lapses, you will need to re-enroll as a new member. There may be a waiting period before coverage begins. It\'s best to renew before your expiry date.'
        },
        {
          question: 'How do I add a newborn to my membership?',
          answer: 'Newborns are automatically covered under the mother\'s membership. Visit your enrollment center with the birth certificate to officially add the baby to your household membership.'
        }
      ]
    };
  },

  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.observeElements();
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    goToPage(page) {
      this.$router.push(page);
    },

    observeElements() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      document.querySelectorAll('.animate-on-scroll').forEach(el => {
        observer.observe(el);
      });
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    handleScroll() {
      this.showScrollTop = window.pageYOffset > 300;
    },

    goToMemberLogin() {
      this.$router.push('/member-login');
    },

    goToStaffLogin() {
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
/* ========================================================================= */
/* ENROLLMENT PAGE - PONA HEALTH-X STYLES */
/* ========================================================================= */

.enrollment-page {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow-x: hidden;
  background: #fafbfc;
}

/* ========================================================================= */
/* NAVIGATION */
/* ========================================================================= */
.navbar {
  padding: 1rem 0;
  transition: all 0.4s ease;
  z-index: 1001;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
}

.nav-link {
  font-weight: 600;
  color: #495057 !important;
  transition: all 0.3s ease;
}

.nav-link:hover,
.nav-link.active {
  color: #2c5aa0 !important;
}

.dropdown-menu {
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-radius: 1rem;
  padding: 0.5rem;
}

.dropdown-item {
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  font-weight: 500;
}

.dropdown-item:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  transform: translateX(5px);
}

/* ========================================================================= */
/* PAGE HEADER */
/* ========================================================================= */
.page-header {
  background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 50%, #4a90e2 100%);
  padding-top: 120px;
  padding-bottom: 60px;
  position: relative;
  overflow: hidden;
}

.header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse"><path d="M 100 0 L 0 0 0 100" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grid)"/></svg>');
}

.z-2 { z-index: 2; }
.min-vh-50 { min-height: 50vh; }

.breadcrumb-light {
  background: transparent;
  margin: 0;
  padding: 0;
}

.breadcrumb-light .breadcrumb-item a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
}

.breadcrumb-light .breadcrumb-item a:hover { color: white; }
.breadcrumb-light .breadcrumb-item.active { color: white; }
.breadcrumb-light .breadcrumb-item + .breadcrumb-item::before { color: rgba(255, 255, 255, 0.5); }

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
}

.header-info-cards { margin-top: 2rem; }

.info-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 1rem 1.5rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.info-card i { font-size: 2rem; color: #4ade80; }
.info-label { display: block; font-size: 0.8rem; color: rgba(255, 255, 255, 0.7); }
.info-value { display: block; font-size: 1.1rem; font-weight: 700; color: white; }

/* ========================================================================= */
/* NOTICE SECTION */
/* ========================================================================= */
.notice-section {
  margin-top: -40px;
  position: relative;
  z-index: 10;
  padding-bottom: 2rem;
}

.notice-card {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
  border-radius: 1.5rem;
  padding: 1.5rem 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-left: 5px solid #ffc107;
}

.notice-icon {
  width: 50px;
  height: 50px;
  background: #ffc107;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #856404;
  flex-shrink: 0;
}

.notice-content h5 { color: #856404; }
.notice-content p { color: #664d03; }

/* ========================================================================= */
/* SECTION LABELS */
/* ========================================================================= */
.section-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.section-label-light {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.label-icon {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #2c5aa0;
}

.section-label-light .label-icon {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.label-text {
  font-weight: 700;
  font-size: 0.9rem;
  color: #2c5aa0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.section-label-light .label-text { color: rgba(255, 255, 255, 0.9); }

.text-gradient {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ========================================================================= */
/* ENROLLMENT IMAGE */
/* ========================================================================= */
.enrollment-image-container { position: relative; }
.main-img { box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15); }

.image-badge {
  position: absolute;
  top: 20px;
  left: 20px;
  background: linear-gradient(135deg, #2c5aa0, #4a90e2);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 8px 25px rgba(44, 90, 160, 0.4);
}

.floating-card {
  position: absolute;
  bottom: -20px;
  right: 20px;
  background: white;
  padding: 1.25rem 1.5rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.fc-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #28a745, #5cb85c);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.fc-number { display: block; font-size: 1.5rem; font-weight: 900; color: #1e3f70; line-height: 1; }
.fc-label { font-size: 0.8rem; color: #6c757d; }

.quick-facts {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

.fact-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.fact-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
}

.bg-success { background: linear-gradient(135deg, #28a745, #5cb85c) !important; }

/* ========================================================================= */
/* ENROLLMENT STEPS */
/* ========================================================================= */
.enrollment-steps { max-width: 800px; margin: 0 auto; }

.step-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  position: relative;
  margin-bottom: 1rem;
  transition: all 0.4s ease;
}

.step-card:hover {
  transform: translateX(10px);
  box-shadow: 0 15px 40px rgba(44, 90, 160, 0.15);
}

.step-number {
  position: absolute;
  top: -15px;
  left: 30px;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2c5aa0, #4a90e2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 1.1rem;
  color: white;
  box-shadow: 0 5px 15px rgba(44, 90, 160, 0.4);
}

.step-icon {
  width: 70px;
  height: 70px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
}

.step-card:hover .step-icon { transform: scale(1.1) rotate(5deg); }

.bg-primary { background: linear-gradient(135deg, #2c5aa0, #4a90e2) !important; }
.bg-info { background: linear-gradient(135deg, #17a2b8, #5bc0de) !important; }
.bg-warning { background: linear-gradient(135deg, #ffc107, #ffda6a) !important; }
.bg-purple { background: linear-gradient(135deg, #6f42c1, #9d7bd1) !important; }
.bg-pink { background: linear-gradient(135deg, #e83e8c, #f8a5c2) !important; }

.step-details { list-style: none; padding: 0; margin: 0; }

.step-details li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  font-size: 0.95rem;
}

.step-details li i { font-size: 1.1rem; }
.step-connector { text-align: center; padding: 0.5rem 0; color: #6c757d; font-size: 1.5rem; }

/* ========================================================================= */
/* LOCATIONS */
/* ========================================================================= */
.location-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  text-align: center;
}

.location-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(44, 90, 160, 0.15);
}

.location-icon {
  width: 80px;
  height: 80px;
  border-radius: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2.5rem;
  color: white;
  transition: all 0.3s ease;
}

.location-card:hover .location-icon { transform: scale(1.1) rotate(5deg); }

.location-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.detail-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.9rem;
}

.detail-item i { color: #2c5aa0; }

.location-services {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.service-tag {
  background: #e3f2fd;
  color: #2c5aa0;
  padding: 0.35rem 0.75rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
}

.find-office-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  padding: 2.5rem;
  border-radius: 1.5rem;
  margin-top: 3rem;
  border: 2px solid #2c5aa0;
}

/* ========================================================================= */
/* DOCUMENTS */
/* ========================================================================= */
.bg-gradient-primary {
  background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 50%, #4a90e2 100%);
}

.documents-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 1.5rem;
  padding: 2rem;
  border: 2px solid rgba(255, 255, 255, 0.15);
  height: 100%;
}

.doc-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.doc-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.doc-list { list-style: none; padding: 0; margin: 0; }
.doc-list li { margin-bottom: 1rem; }

.doc-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.doc-item i { font-size: 1.25rem; margin-top: 2px; }
.doc-name { display: block; color: white; font-weight: 600; }
.doc-note { display: block; color: rgba(255, 255, 255, 0.7); font-size: 0.85rem; }

.doc-tip {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 1rem;
  margin-top: 2rem;
}

.doc-tip i { font-size: 2rem; color: #ffc107; }
.doc-tip p { color: rgba(255, 255, 255, 0.9); }

/* ========================================================================= */
/* PREMIUM PLANS */
/* ========================================================================= */
.premium-card {
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  border: 2px solid transparent;
  position: relative;
}

.premium-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(44, 90, 160, 0.15);
}

.premium-card.featured {
  border-color: #2c5aa0;
  transform: scale(1.05);
}

.premium-card.featured:hover { transform: scale(1.05) translateY(-10px); }

.premium-badge {
  position: absolute;
  top: 0;
  right: 20px;
  background: linear-gradient(135deg, #2c5aa0, #4a90e2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0 0 0.5rem 0.5rem;
  font-weight: 700;
  font-size: 0.8rem;
}

.premium-header {
  padding: 2rem 1.5rem 1rem;
  text-align: center;
}

.premium-icon {
  width: 70px;
  height: 70px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 2rem;
  color: white;
}

.premium-price {
  text-align: center;
  padding: 1rem;
  background: #f8f9fa;
}

.premium-price .currency { font-size: 1.25rem; font-weight: 700; color: #6c757d; }
.premium-price .amount { font-size: 3rem; font-weight: 900; color: #1e3f70; margin: 0 0.25rem; }
.premium-price .period { font-size: 1rem; color: #6c757d; }

.premium-description {
  padding: 1rem 1.5rem;
  text-align: center;
}

.premium-description p { color: #6c757d; margin: 0; font-size: 0.9rem; }

.premium-features { list-style: none; padding: 0 1.5rem; margin: 0; }

.premium-features li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  font-size: 0.9rem;
}

.premium-features li i { font-size: 1.1rem; }

.premium-eligibility {
  padding: 1rem 1.5rem 1.5rem;
  background: #f8f9fa;
  margin-top: 1rem;
}

.eligibility-label { font-weight: 700; color: #1e3f70; display: block; margin-bottom: 0.25rem; font-size: 0.85rem; }
.eligibility-text { color: #6c757d; font-size: 0.9rem; }

.payment-info-card {
  background: white;
  padding: 2.5rem;
  border-radius: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  margin-top: 3rem;
}

.payment-methods {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-radius: 1rem;
  transition: all 0.3s ease;
}

.payment-method:hover {
  background: #e3f2fd;
  transform: translateY(-3px);
}

.payment-method i { font-size: 1.5rem; color: #2c5aa0; }
.payment-method span { font-weight: 600; }

.payment-schedule { list-style: none; padding: 0; margin: 0; }

.payment-schedule li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
}

/* ========================================================================= */
/* ELIGIBILITY */
/* ========================================================================= */
.eligibility-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.eligibility-card.eligible { border-top: 4px solid #28a745; }
.eligibility-card.not-eligible { border-top: 4px solid #17a2b8; }

.elig-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.elig-header i { font-size: 1.5rem; }
.eligible .elig-header i { color: #28a745; }
.not-eligible .elig-header i { color: #17a2b8; }

.elig-list { list-style: none; padding: 0; margin: 0; }

.elig-list li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
}

.eligible .elig-list li i { color: #28a745; }
.not-eligible .elig-list li i { color: #17a2b8; }

.elig-note {
  background: #e8f4f8;
  padding: 1rem;
  border-radius: 0.75rem;
  margin-top: 1.5rem;
}

.elig-note p { color: #0c5460; font-size: 0.9rem; }

/* ========================================================================= */
/* FAMILY COVERAGE */
/* ========================================================================= */
.family-coverage-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.coverage-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: #f8f9fa;
  border-radius: 1rem;
  transition: all 0.3s ease;
}

.coverage-item:hover {
  background: #e3f2fd;
  transform: translateX(10px);
}

.coverage-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.coverage-content { flex: 1; }

.status-badge {
  padding: 0.35rem 0.75rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 700;
}

.status-badge.included { background: #d4edda; color: #28a745; }
.status-badge.eligible { background: #fff3cd; color: #856404; }

.family-illustration {
  position: relative;
  height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.family-center {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #2c5aa0, #4a90e2);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  box-shadow: 0 10px 30px rgba(44, 90, 160, 0.4);
  z-index: 2;
}

.family-icon { font-size: 2.5rem; margin-bottom: 0.25rem; }
.family-center span { font-size: 0.8rem; font-weight: 600; }

.family-member {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.member-icon {
  width: 50px;
  height: 50px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #2c5aa0;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.family-member span { font-size: 0.75rem; font-weight: 600; color: #6c757d; }

/* ========================================================================= */
/* RENEWAL */
/* ========================================================================= */
.renewal-card {
  background: white;
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: all 0.4s ease;
}

.renewal-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 50px rgba(44, 90, 160, 0.15);
}

.renewal-icon {
  width: 70px;
  height: 70px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: white;
  transition: all 0.3s ease;
}

.renewal-card:hover .renewal-icon { transform: scale(1.1) rotate(5deg); }

.renewal-reminder {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  background: white;
  padding: 2rem;
  border-radius: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  margin-top: 3rem;
  border-left: 5px solid #ffc107;
}

.reminder-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ffc107, #ffda6a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: white;
  flex-shrink: 0;
}

/* ========================================================================= */
/* FAQ */
/* ========================================================================= */
.accordion-item {
  margin-bottom: 1rem;
  border: none;
  border-radius: 1rem !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.accordion-button {
  font-weight: 600;
  color: #1e3f70;
  background-color: white;
  padding: 1.25rem 1.5rem;
}

.accordion-button:not(.collapsed) {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1e3f70;
  box-shadow: none;
}

.accordion-button:focus { box-shadow: none; }
.accordion-body { padding: 1.5rem; line-height: 1.8; color: #495057; }

/* ========================================================================= */
/* CTA SECTION */
/* ========================================================================= */
.cta-section { background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 100%); }

.cta-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 1.5rem;
  padding: 3rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.cta-features {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.cta-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
}

.cta-feature i { color: #4ade80; font-size: 1.25rem; }

/* ========================================================================= */
/* BUTTONS */
/* ========================================================================= */
.btn {
  padding: 0.875rem 2rem;
  border-radius: 0.75rem;
  font-weight: 700;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-color: transparent;
  color: white;
}

.btn-primary:hover { background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 100%); }
.btn-light { background: white; color: #2c5aa0; }
.btn-outline-light { color: white; border-color: white; }
.btn-outline-light:hover { background: white; color: #2c5aa0; }

/* ========================================================================= */
/* FOOTER */
/* ========================================================================= */
footer { background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 100%); }
.footer-links { list-style: none; padding: 0; }
.footer-links li { margin-bottom: 0.5rem; }

.footer-links a {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: all 0.3s ease;
}

.footer-links a:hover { color: white; padding-left: 8px; }
.footer-contact { list-style: none; padding: 0; }

.footer-contact li {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.social-links { display: flex; gap: 1rem; }

.social-links .social-link {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  transition: all 0.3s ease;
}

.social-links .social-link:hover {
  background: white;
  color: #2c5aa0;
  transform: translateY(-5px);
}

/* ========================================================================= */
/* SCROLL BUTTON */
/* ========================================================================= */
.scroll-top-btn {
  position: fixed;
  bottom: 35px;
  right: 35px;
  width: 55px;
  height: 55px;
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(44, 90, 160, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;
  font-size: 1.8rem;
}

.scroll-top-btn:hover {
  transform: translateY(-8px) scale(1.1);
  box-shadow: 0 15px 40px rgba(44, 90, 160, 0.6);
}

/* ========================================================================= */
/* ANIMATIONS */
/* ========================================================================= */
.animate-on-scroll {
  opacity: 0;
  transform: translateY(40px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.animate-on-scroll.animate-in {
  opacity: 1;
  transform: translateY(0);
}

/* ========================================================================= */
/* RESPONSIVE */
/* ========================================================================= */
@media (max-width: 992px) {
  .premium-card.featured { transform: none; }
  .premium-card.featured:hover { transform: translateY(-10px); }
  .family-illustration { display: none; }
}

@media (max-width: 768px) {
  .page-header { padding-top: 100px; }
  .page-header h1 { font-size: 2.5rem; }
  .notice-card { flex-direction: column; text-align: center; }
  .info-card { width: 100%; }
  .floating-card { display: none; }
  .cta-features { flex-direction: column; gap: 1rem; }
  .find-office-card { text-align: center; }
}
</style>